import React, { useEffect, useRef, useState } from "react";
import { MessageSquareXIcon, BadgeCheck } from "lucide-react";
import { Feedback } from "@/interfaces/chat.interface";
import {
  MESSAGE_FEEDBACK,
  MESSAGE_FEEDBACK_POLICY,
  MessageFeedbackType,
  ConversationType,
} from "@/constants/chat.constant";
import toast from "react-hot-toast";

/**
 * Props for the MessageFeedbackInput component
 */
interface MessageFeedbackInputProps {
  /** Callback function called when feedback is submitted */
  onFeedbackSent: (newFeedback: Feedback) => void;
  /** Type of feedback (positive or negative) */
  feedbackType: MessageFeedbackType;
  /** Whether the input box is open. Defaults to false. */
  open?: boolean;
  /** CHOP GPT or Policy Assistant Determines message feedback buttons */
  conversationType?: string;
}

/**
 * MessageFeedbackInput component displays a form to collect detailed message feedback.
 * Shows tags specific to the feedback type (positive/negative) and a textarea for
 * additional comments. Limits text input to 300 characters. Auto-focuses on open.
 * 
 * @component
 * @param {MessageFeedbackInputProps} props - Component props
 * @param {(newFeedback: Feedback) => void} props.onFeedbackSent - Callback fired when feedback submitted
 * @param {MessageFeedbackType} props.feedbackType - Type of feedback being collected
 * @param {boolean} [props.open=false] - Whether the input is open
 * @returns {JSX.Element} Feedback input form with tags and textarea
 * 
 * @example
 * ```tsx
 * <MessageFeedbackInput 
 *   feedbackType={MessageFeedbackType.POSITIVE}
 *   onFeedbackSent={handleSubmit}
 *   open={true}
 * />
 * ```
 */
export default function MessageFeedbackInput({
  feedbackType,
  onFeedbackSent,
  open,
  conversationType,
}: MessageFeedbackInputProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const message_feedback = conversationType == ConversationType.POLICY_ASSISTANT ? MESSAGE_FEEDBACK_POLICY : MESSAGE_FEEDBACK;
  const feedbackContent = message_feedback[feedbackType];

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  useEffect(() => {
    if (open && containerRef.current) {
      const timer = setTimeout(() => {
        const firstFocusable = containerRef?.current?.querySelector<
          HTMLButtonElement | HTMLTextAreaElement
        >(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        );

        firstFocusable?.focus({ preventScroll: true });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSubmitFeedback = async () => {
    try {
      setIsSubmitting(true);

      const request: Feedback = {
        feedback_type: feedbackType,
        feedback_text: {
          message: feedbackText || undefined,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
        },
      };

      await onFeedbackSent(request);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Error submitting feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="font-primary md:w-[28rem] w-full bg-modal-background rounded-lg shadow-xl border border-gray-200 p-4 md:p-3 md:pb-2"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3 text-primary-500">
        <div className="w-6 h-6 md:mr-0 mr-[4px]">
          {feedbackType == MessageFeedbackType.POSITIVE ? (
            <BadgeCheck />
          ) : (
            <MessageSquareXIcon />
          )}
        </div>
        <div className="text-sm self-end  [@media(max-height:670px)]:text-xs">
          {feedbackContent.header}
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {feedbackContent.tags &&
          feedbackContent.tags.map((tag: any) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`
              px-2 py-1 rounded-full md:text-sm text-xxs font-medium transition-all duration-200 focus:outline-none focus:ring-0
              ${
                selectedTags.includes(tag)
                  ? "bg-primary-100 text-primary-500 border-2 border-primary-500"
                  : "text-[#3A3A3A] border-2 border-[#AFB4C5]"
              }
            `}
            >
              {tag}
            </button>
          ))}
      </div>

      {/* Feedback Text Input */}
      <div className="mb-2">
        <textarea
          value={feedbackText}
          onChange={(e) => {
            if(e.target.value.length <= 300)
              setFeedbackText(e.target.value)
          }}
          placeholder={feedbackContent.placeholder}
          className="placeholder:text-sm md:placeholder:text-base font-secondary w-full min-h-[100px] p-4 text-base text-gray-900 border-2 border-gray-200 rounded-lg resize-none focus:outline-none focus:border-primary-200"
        />
        <div className="flex justify-end text-xs font-secondary">
          {300 - feedbackText.length} Characters
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmitFeedback}
        disabled={isSubmitting}
        className={`w-full py-2 text-sm [@media(max-height:670px)]:text-xs rounded-lg transition-colors duration-200 ${
          isSubmitting
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-primary-100 text-primary-500 hover:bg-primary-100/80"
        }`}
      >
        {isSubmitting ? "Submitting..." : "Share feedback"}
      </button>
    </div>
  );
}
